import { Button, useToast } from "@chakra-ui/react";
import FormModal from "../../../components/FormModal";
import { useState } from "react";
import { v4 } from 'uuid';
import { translate } from "../../../i18n";
import { CoachAvailabilityDto } from "../../../models/coach/dto/coach-availability.dto";
import { DayOfWeek } from "../../../models/court/dto/batch-upsert-availabilities.dto";
import { CreateCourtFormAvailabilityInputType, MINUTES_PER_SLOT, SLOTS_PER_HOUR } from "../../../models/court/dto/court-availability-template.constants";
import { SportType } from "../../../models/court/dto/court.dto";
import AddAvailabilitiesForm, { getDatesInRange, splitRangeIntoSlots } from "../../court-management/components/AddAvailabilitiesForm";
import { createDateObjects } from "../../court-management/components/CreateCourtForm";
import { getDefaultCurrency } from "../../court-pricing-management/components/CurrencyPicker";
import { useSetCoachAvailabilities } from "../hooks/useSetCoachAvailabilities";

const ONE_HOUR_MS = 3600000;

const getNewDefaultAvailability = (): CreateCourtFormAvailabilityInputType => ({
    id: v4(), daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
    min: 8 * SLOTS_PER_HOUR,
    max: 12 * SLOTS_PER_HOUR,
    slotDurationInMs: ONE_HOUR_MS,
    configurations: [{
        sport: SportType.PADEL,
        price: 10,
        currency: getDefaultCurrency(),
        id: v4(),
        slotDurationInMs: ONE_HOUR_MS
    }]
});
export default function SetCoachAvailabilitiesModal({ coachId }: { coachId: string }) {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [availabilities, setAvailabilities] = useState<CreateCourtFormAvailabilityInputType[]>(() => [getNewDefaultAvailability()]);
    const toast = useToast();
    const { mutateAsync: batchUpsertAvailabilities, isLoading } = useSetCoachAvailabilities(coachId, () => {
        setIsModalVisible(false);
        toast({
            title: translate('availabilities_edited_successfully'),
            status: 'success',
            duration: 3000,
            position: 'top-right'
        });
    });

    const [returnAsOneChunk, setReturnAsOneChunk] = useState(false);
    const [startDate, setStartDate] = useState(() => {
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        return date;
    });
    const [endDate, setEndDate] = useState(() => {
        const date = new Date(new Date().getTime() + 364 * 24 * 60 * 60 * 1000);
        date.setHours(23, 59, 59, 99)
        return date;
    });

    const onRemoveAvailability = () => {
        setAvailabilities(availabilities.slice(1));
    };
    const onAddAvailability = () => {
        setAvailabilities([...availabilities, getNewDefaultAvailability()]);
    };

    const handleSubmit = () => {
        const av: CoachAvailabilityDto[] = [];
        availabilities.forEach(a => {

            a.daysOfWeek.forEach(d => {
                const startTime = createDateObjects({ dayOfWeek: d, startHour: Math.floor(a.min / SLOTS_PER_HOUR), startMinute: (a.min % SLOTS_PER_HOUR) * MINUTES_PER_SLOT, endHour: Math.floor(a.max / SLOTS_PER_HOUR), endMinute: (a.max % SLOTS_PER_HOUR) * MINUTES_PER_SLOT }).startDate.toISOString();
                const endTime = createDateObjects({ dayOfWeek: d, startHour: Math.floor(a.min / SLOTS_PER_HOUR), startMinute: (a.min % SLOTS_PER_HOUR) * MINUTES_PER_SLOT, endHour: Math.floor(a.max / SLOTS_PER_HOUR), endMinute: (a.max % SLOTS_PER_HOUR) * MINUTES_PER_SLOT }).endDate.toISOString();

                const slots = splitRangeIntoSlots([new Date(startTime), new Date(endTime)], a.slotDurationInMs, returnAsOneChunk);
                slots.forEach(slot => {
                    av.push({
                        id: v4(),
                        dayOfWeek: d,
                        startTime: slot[0].toISOString(),
                        endTime: slot[1].toISOString(),
                        coachId,
                    })
                })
            }
            )
        });

        const _availabilities = getDatesInRange(startDate, endDate, av.map(item => {
            return {
                dayOfWeek: (item.dayOfWeek as any) as DayOfWeek,
                startHour: new Date(item.startTime).getHours(),
                startMin: new Date(item.startTime).getMinutes(),
                endHour: new Date(item.endTime).getHours(),
                endMin: new Date(item.endTime).getMinutes(),
                pricings: [] as any
            };
        }))
        batchUpsertAvailabilities({
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            availabilities: _availabilities.map(([startTime, endTime, pricings]) => {
                return {
                    coachId,
                    dayOfWeek: startTime.getDay(),
                    startTime: startTime.toISOString(),
                    endTime: endTime.toISOString(),
                    id: v4(),
                    pricings
                }
            }),
            coachId
        }).catch((error: Error) => {
            toast({
                title: translate('error'),
                description: error?.message || translate('error'),
                status: 'error',
                duration: 5000,
                position: 'top-right'
            });
        })
    };

    return (
        <>
            <Button width={60} colorScheme="brand" onClick={() => { setIsModalVisible(true) }}>
                {translate('court.add_availabilities')}
            </Button>
            <FormModal
                isOpen={isModalVisible}
                onClose={() => setIsModalVisible(false)}
                title={translate('court.add_availabilities')}
                onSubmit={handleSubmit}
                submitLabel={translate('submit')}
                isSubmitting={isLoading}
                size="xl"
            >
                        <AddAvailabilitiesForm
                            startDate={startDate}
                            setStartDate={setStartDate}
                            endDate={endDate}
                            setEndDate={setEndDate}
                            onAddAvailability={onAddAvailability}
                            onRemoveAvailability={onRemoveAvailability}
                            availabilities={availabilities}
                            onAvailabilitiesChange={setAvailabilities}
                            returnAsOneChunk={returnAsOneChunk}
                            setReturnAsOneChunk={setReturnAsOneChunk} />
            </FormModal>
        </>
    )
}
